/* UTILS */
import { computed, inject, ref, nextTick } from "vue"
import { defineStore, storeToRefs } from "pinia"
import type { Translate } from "@/plugins/i18n/types"
import { useCookies } from "@vueuse/integrations/useCookies"
import { COOKIES } from "@/config/constants"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"

/* STORES */
import { useConfigStore } from "@/stores/config"
import { useUserStore } from "@/stores/user"
import { useI18nStore } from "@/stores/i18n"

export const useAnswersStore = defineStore("screeningAnswersStore", () => {
  const t = inject("t") as Translate
  const { urls } = useConfigStore()
  const userStore = useUserStore()
  const i18nStore = useI18nStore()

  const { user, nextAssessment } = storeToRefs(userStore)
  const { fetchNextAssessment } = userStore
  const cookies = useCookies()
  const { currentLocale, currentLang } = storeToRefs(i18nStore)
  const { getLocaleIdFromIsoCode } = i18nStore

  const isConsentNeeded = user.value.permissions.includes("cst_na")
  const screeningLang = computed(() =>
    getLocaleIdFromIsoCode(currentLocale.value.iso_code)
  )

  const SCREENING_COOKIE_OPTIONS = {
    domain: useConfigStore().urls.cookie_domain,
    httpOnly: false,
    secure: true,
    sameSite: "none" as const,
  }

  // STORAGE DATA
  // We store the microphone and speaker id in the local storage to keep the user choice
  // In case of refresh or tab close, the user will have the same choice
  const selectedMicrophoneId = ref<string>(
    localStorage.getItem("selectedMicrophoneId") || ""
  )
  const selectedSpeakerId = ref<string>(
    localStorage.getItem("selectedSpeakerId") || ""
  )

  const screeningToken = ref<string>(
    localStorage.getItem("screening-token") || ""
  )

  const nextAssessmentExtraData = computed(
    () => nextAssessment.value?.next_assessment?.extra_data
  )

  // COOKIES DATA
  const avatarType = computed(() => {
    const AVATAR_FEMALE_ID = 1

    const avatarId =
      cookies.get(COOKIES.SCREENING_AGENT_HR) ||
      nextAssessmentExtraData.value?.agent_hr

    if (![1, 2].includes(Number(avatarId))) {
      return "f"
    }

    const isAvatarFemale = avatarId === AVATAR_FEMALE_ID

    return isAvatarFemale ? "f" : "m"
  })

  const companyName = computed(() =>
    cookies.get(COOKIES.SCREENING_COMPANY_NAME)
  )

  const companyNameWithSharepool = computed(() => {
    const companyLabel = companyName.value?.trim().toLowerCase()
    const sharepoolLabel =
      cookies.get(COOKIES.SCREENING_SHAREPOOL_LABEL) ??
      nextAssessmentExtraData.value?.sharepool_label
    const sharepoolLabelCleaned = sharepoolLabel?.trim().toLowerCase()

    if (
      companyLabel === sharepoolLabelCleaned ||
      sharepoolLabelCleaned?.includes(companyLabel)
    ) {
      return sharepoolLabel ?? companyName.value
    }

    if (
      !sharepoolLabelCleaned ||
      companyLabel.includes(sharepoolLabelCleaned)
    ) {
      return companyName.value
    }

    return `${companyName.value} - ${sharepoolLabel}`
  })

  const pmUuid = computed(() => cookies.get(COOKIES.PM_UUID))

  const roleName = computed(() => cookies.get(COOKIES.SCREENING_PM_NAME))

  const isCVRequired = computed<boolean>(() => {
    return nextAssessmentExtraData.value?.cv_required ?? false
  })

  const avatarUrl = computed(() =>
    avatarType.value === "m"
      ? `${urls.assets}/img/avatar-man.png`
      : `${urls.assets}/img/avatar-woman.png`
  )

  const avatarSmallUrl = computed(() =>
    avatarType.value === "m"
      ? `${urls.assets}/img/avatar-man-small.png`
      : `${urls.assets}/img/avatar-woman-small.png`
  )

  const agentName = computed(() => {
    currentLocale.value // Make it more dynamic using the locale change
    return avatarType.value === "m"
      ? t("general.agent.male_01")
      : t("general.agent.female_01")
  })

  const trackMixpanelEvent = (
    eventName: string,
    entryPoint: string,
    extra: Record<string, unknown> = {}
  ) => {
    MixpanelInstance.track(eventName, {
      ...extra,
      entry_point: entryPoint,
      language: currentLang.value,
      assessment: "scan",
    })
  }

  const fetchScreeningData = async () => {
    const pm_uuid = useCookies().get(COOKIES.PM_UUID)
    const company_uuid = useCookies().get(COOKIES.COMPANY_UUID)

    await fetchNextAssessment(pm_uuid ?? null, company_uuid ?? null)
    await nextTick()
  }

  const setMicrophoneId = (id: string) => {
    selectedMicrophoneId.value = id
    localStorage.setItem("selectedMicrophoneId", id)
  }

  const setSpeakerId = (id: string) => {
    selectedSpeakerId.value = id
    localStorage.setItem("selectedSpeakerId", id)
  }

  const setScreeningToken = (token: string) => {
    screeningToken.value = token
    localStorage.setItem("screening-token", token)
  }

  const clearScreeningStorage = () => {
    localStorage.removeItem("selectedMicrophoneId")
    localStorage.removeItem("selectedSpeakerId")
    localStorage.removeItem("screening-token")
  }
  const clearScreeningCookies = () => {
    cookies.remove(COOKIES.SCREENING_AGENT_HR, SCREENING_COOKIE_OPTIONS)
    cookies.remove(COOKIES.SCREENING_PM_NAME, SCREENING_COOKIE_OPTIONS)
    cookies.remove(COOKIES.SCREENING_COMPANY_NAME, SCREENING_COOKIE_OPTIONS)
    cookies.remove(COOKIES.SCREENING_SHAREPOOL_LABEL, SCREENING_COOKIE_OPTIONS)
  }

  return {
    avatarUrl,
    avatarType,
    avatarSmallUrl,
    companyName,
    companyNameWithSharepool,
    agentName,
    selectedMicrophoneId,
    selectedSpeakerId,
    screeningToken,
    pmUuid,
    roleName,
    screeningLang,
    setMicrophoneId,
    setSpeakerId,
    setScreeningToken,
    clearScreeningStorage,
    isConsentNeeded,
    isCVRequired,
    clearScreeningCookies,
    fetchScreeningData,
    trackMixpanelEvent,
  }
})
